/**
 * This code was  originally copied from the lexical playground source code on github
 * And so it doesn't follow our code style
 * https://github.com/facebook/lexical/tree/main/packages/lexical-playground
 *
 */

import { addClassNamesToElement, removeClassNamesFromElement } from "@lexical/utils";
import { isContractValidationPage } from "helpers/utils";
import type { EditorConfig, LexicalNode, NodeKey, SerializedTextNode, Spread } from "lexical";
import { $applyNodeReplacement, createCommand, TextNode } from "lexical";

export const MARK_AS_REFERENCE_COMMAND = createCommand<void>();

type SerializedReferenceNode = Spread<
    {
        error: boolean;
        articleReferenceKey: string;
    },
    SerializedTextNode
>;

export class ReferenceNode extends TextNode {
    __articleReferenceKey: string = "";
    __error: boolean = false;

    getArticleReferenceKey(): string {
        const self = this.getLatest();
        return self.__articleReferenceKey;
    }

    getError(): boolean {
        const self = this.getLatest();
        return self.__error;
    }

    setArticleReferenceKey(value: string) {
        const self = this.getWritable();
        self.__articleReferenceKey = value;
    }

    setError(value: boolean) {
        const self = this.getWritable();
        self.__error = value;
    }

    static getType(): string {
        return "reference";
    }

    static clone(node: ReferenceNode): ReferenceNode {
        return new ReferenceNode(node.__text, node.__articleReferenceKey, node.__error, node.__key);
    }

    constructor(text: string, articleReferenceKey: string, error: boolean, key?: NodeKey) {
        super(text, key);
        this.__articleReferenceKey = articleReferenceKey;
        this.__error = error;
    }

    createDOM(config: EditorConfig): HTMLElement {
        const element = super.createDOM(config);
        const cssClass = isContractValidationPage() ? config.theme.referenceSoft : config.theme.reference;
        addClassNamesToElement(element, cssClass);
        return element;
    }

    updateDOM(prevNode: TextNode, dom: HTMLElement, config: EditorConfig): boolean {
        const cssClass = isContractValidationPage() ? config.theme.referenceSoftError : config.theme.referenceError;
        if (this.getError()) addClassNamesToElement(dom, cssClass);
        else removeClassNamesFromElement(dom, cssClass);
        super.updateDOM(prevNode, dom, config);
        return false;
    }

    static importJSON(serializedNode: SerializedReferenceNode): ReferenceNode {
        const node = $createReferenceNode(
            serializedNode.text,
            serializedNode.articleReferenceKey,
            serializedNode.error,
        );
        node.setFormat(serializedNode.format);
        node.setDetail(serializedNode.detail);
        node.setMode(serializedNode.mode);
        node.setStyle(serializedNode.style);
        return node;
    }

    exportJSON(): SerializedReferenceNode {
        return {
            ...super.exportJSON(),
            error: this.__error,
            articleReferenceKey: this.__articleReferenceKey,
            type: "reference",
        };
    }

    canInsertTextBefore(): boolean {
        return false;
    }

    isTextEntity(): true {
        return true;
    }
}

export function $createReferenceNode(text = "", articleReferenceKey = "", error = false): ReferenceNode {
    return $applyNodeReplacement(new ReferenceNode(text, articleReferenceKey, error));
}

/**
 * Determines if node is a ReferenceNode.
 * @param node - The node to be checked.
 * @returns true if node is a ReferenceNode, false otherwise.
 */
export function $isReferenceNode(node: LexicalNode | null | undefined): node is ReferenceNode {
    return node instanceof ReferenceNode;
}
